import * as React from 'react';
import { Drawer } from '@mui/material';
import {
  TextField,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  ListItemSecondaryAction
} from '@mui/material';
import { Clear as ClearIcon } from '@mui/icons-material';
import {
  withStyles,
  WithStyles,
  StyleRules
} from '@mui/styles';
import { Icon } from '@mui/material';
import { Switch } from '@mui/material';
import { Divider } from '@mui/material';
import StarBorderOutlinedIcon from '@mui/icons-material/StarBorderOutlined';
import StarIcon from '@mui/icons-material/Star';

import { Theme } from '@mui/styles';

import PortalUserContext, { FeatureArea } from '~common/lib/portal-user-context'

/** Cookie storing JSON array of starred menu feature ids (see featureStarId in render). */
const STARRED_FEATURES_COOKIE = 'icgMenuDrawerStarredFeatures';
/** Sliding window: each write refreshes expiry (toggle star or open a favorited item). */
const STARRED_FEATURES_MAX_AGE_SECONDS = 60 * 60 * 24 * 90;

// This will eventually be pulled from the server
var resource = {}
async function fetchStaticResources() {
  try {
    const response = await fetch("/static/i18n/i18n.json"); // Fetch the file
    if (!response.ok) {
      // throw new Error(`HTTP error status: ${response.status}`);
    } else {

      const data = await response.text();
      resource = JSON.parse(data || "{}");
    }
  } catch (error) {
    console.error('Error fetching JSON file:', error);
  }
}

async function loadResourceFile() {
  await fetchStaticResources()
}


loadResourceFile();


function translate(text, lang) {
  var returnText = text;
  if (text) {
    var searchText = text.toLowerCase().split(" ").join("")
    if (!lang) {
      lang = "en"
      if (window.App && window.App.userContext) {
        if (window.App.userContext.extendedAttributes && window.App.userContext.extendedAttributes.preferredLanguage) {
          lang = window.App.userContext.extendedAttributes.preferredLanguage
        }
      }
    }

    if (resource[lang]) {
      const r = resource[lang]
      if (r[searchText]) {
        returnText = resource[lang][searchText]
      }
    }
  }
  return returnText;
}

// import { each, sortBy } from 'lodash';

export interface MenuDrawerProps extends React.HTMLProps<HTMLDivElement> {
  open: boolean;
  history: any;
  onClose?: Function;
  invertToolbar?: boolean;
}

const styles = (theme: Theme): StyleRules => ({
  drawerWidth: {
    width: 400
  },
  icon: {
    fontSize: 20,
    marginRight: 5,
    color: theme.palette.primary.main + " !important",
    minWidth: "35px !important"
  },
  featureAreaToggle: {
    color: "#bdbdbd" + " !important",
    float: "right",
    cursor: 'pointer'
  },
  faicon: {
    fontSize: 20,
    marginRight: 5,
    color: theme.palette.primary.main + " !important",
    minWidth: "35px !important"
  },
  /** Nested lists under a feature area: indent to match outline hierarchy. */
  nestedFeatureList: {
    paddingLeft: theme.spacing(7)
  },
  /** Subheaders inside nested feature lists; base indent comes from nestedFeatureList. */
  nestedListSubheader: {
    marginLeft: theme.spacing(-3)
  },
  menuItem: {
    fontSize: theme.typography.body1.fontSize,
    fontWeight: 500
  },
  menuItemDescription: {
    color: theme.palette.grey['600'],
    fontSize: '0.95rem',
    fontWeight: 400
  },
  featureArea: {
    cursor: "pointer",
  },
  featureStarButton: {
    padding: theme.spacing(0.5),
    color: 'inherit'
  },
  /** Favorite feature rows nested under the Favorites row (same indent as feature-area children). */
  favoritesNestedList: {
    paddingLeft: theme.spacing(7)
  }
});

type State = {
  drawerOpen: boolean;
  showVerboseMenus?: boolean;
  filteringDisabled?: boolean;
  featureAreaVisibility: any;
  filterText?: string;
  /** Keys from feature `key` or URL; toggles StarBorder vs Star without navigating. */
  starredFeatures: Record<string, boolean>;
  /** When true, show StarIcon / StarBorderOutlined on feature rows; always starts false each load (not cookie-persisted). */
  showMenuStars: boolean;
};

class MenuDrawer extends React.Component<
  MenuDrawerProps & WithStyles<keyof typeof styles>,
  State
> {
  constructor(props: MenuDrawerProps & WithStyles<keyof typeof styles>) {
    super(props, {});
    // Check for menu collaps cookie

    var menuState: any = {}
    var filteringDisabled: boolean = false;
    menuState = this.getFeatureAreaVisibilityCookie()
    document.cookie =
      'menuDrawerShowStars=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    // Attention: Using preferences
    const newState = {
      drawerOpen: false,
      featureAreaVisibility: menuState,
      filteringDisabled: this.getFilteringDisabledCookie() || false,
      starredFeatures: this.getStarredFeaturesCookie(),
      showMenuStars: false,
    };

    this.state = newState;
  }

  setStarredFeaturesCookie = (starredFeatures: Record<string, boolean>) => {
    const ids = Object.keys(starredFeatures).filter((k) => starredFeatures[k]);
    document.cookie =
      STARRED_FEATURES_COOKIE +
      '=' +
      JSON.stringify(ids) +
      ';path=/' +
      ';max-age=' +
      STARRED_FEATURES_MAX_AGE_SECONDS;
  };

  getStarredFeaturesCookie = (): Record<string, boolean> => {
    try {
      const name = STARRED_FEATURES_COOKIE + '=';
      const decodedCookie = decodeURIComponent(document.cookie);
      const ca = decodedCookie.split(';');
      for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') {
          c = c.substring(1);
        }
        if (c.indexOf(name) === 0) {
          const raw = c.substring(name.length, c.length);
          const cookieData = JSON.parse(raw);
          const r: Record<string, boolean> = {};
          if (Array.isArray(cookieData)) {
            cookieData.forEach((id: unknown) => {
              if (typeof id === 'string' && id.length) {
                r[id] = true;
              }
            });
            return r;
          }
          if (cookieData && typeof cookieData === 'object') {
            Object.keys(cookieData).forEach((k) => {
              if ((cookieData as Record<string, boolean>)[k]) {
                r[k] = true;
              }
            });
            return r;
          }
        }
      }
    } catch {
      document.cookie =
        STARRED_FEATURES_COOKIE +
        '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      return {};
    }
    return {};
  };

  setFeatureAreaVisibilityCookie = (data: any) => {
    document.cookie = "featureAreaVisibility=" + JSON.stringify(data) + ";path=/";
  }

  setFilteringDisabledCookie = (data: any) => {
    document.cookie = "filteringDisabled=" + this.state.filteringDisabled + ";path=/";
  }
  getFilteringDisabledCookie = () => {
    try {
      var name = "filteringDisabled=";
      var decodedCookie = decodeURIComponent(document.cookie);
      var ca = decodedCookie.split(';');
      for (var i = 0; i < ca.length; i++) {
        var c = ca[i];
        while (c.charAt(0) == ' ') {
          c = c.substring(1);
        }
        if (c.indexOf(name) == 0) {
          const cookieData = JSON.parse(c.substring(name.length, c.length));
          return cookieData;
        }
      }
    } catch {
      // Cleanup the cookie if it cannot be parsed
      document.cookie = "filteringDisabled=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
      return false
    }
    return false
  }

  getFeatureAreaVisibilityCookie = () => {
    try {
      var name = "featureAreaVisibility=";
      var decodedCookie = decodeURIComponent(document.cookie);
      var ca = decodedCookie.split(';');
      for (var i = 0; i < ca.length; i++) {
        var c = ca[i];
        while (c.charAt(0) == ' ') {
          c = c.substring(1);
        }
        if (c.indexOf(name) == 0) {
          const cookieData = JSON.parse(c.substring(name.length, c.length));
          return cookieData;
        }
      }
    } catch {
      // Cleanup the cookie if it cannot be parsed
      document.cookie = "featureAreaVisibility=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
      return {}
    }
    return {}
  }

  UNSAFE_componentWillReceiveProps(
    newProps: MenuDrawerProps & WithStyles<keyof typeof styles>
  ) {
    const uc = PortalUserContext.userContext;

    var verbose: boolean | undefined;

    if (this.state.showVerboseMenus == undefined) {
      verbose = uc.extendedAttributes.showVerboseMenus
        ? uc.extendedAttributes.showVerboseMenus
        : undefined;
    } else {
      verbose = this.state.showVerboseMenus;
    }

    this.setState({ drawerOpen: newProps.open, showVerboseMenus: verbose });
  }

  toggleDrawer = (open: boolean) => () => {
    this.setState({
      drawerOpen: open
    });

    if (this.props.onClose && !open) {
      this.props.onClose();
    }
  };

  toggleCompact = () => {
    this.setState({
      showVerboseMenus: !this.state.showVerboseMenus
    });
  };

  toggleFiltering = () => {
    this.setState({
      filteringDisabled: !this.state.filteringDisabled
    }, () => {
      if (this.state.filteringDisabled) {
        this.setState({
          filterText: ''
        });
      }
      this.setFilteringDisabledCookie(this.state.filteringDisabled)
    });
    
  };

  toggleShowMenuStars = () => {
    this.setState((prev) => ({ showMenuStars: !prev.showMenuStars }));
  };

  handleMenuItemClick =
    (url: string, extendStarredCookie?: boolean) => () => {
      if (extendStarredCookie) {
        this.setStarredFeaturesCookie(this.state.starredFeatures);
      }
      window.location.href = url;
    };

  toggleFeatureStar = (featureId: string) => (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    this.setState(
      (prev) => {
        const next = { ...prev.starredFeatures };
        if (next[featureId]) {
          delete next[featureId];
        } else {
          next[featureId] = true;
        }
        return { starredFeatures: next };
      },
      () => this.setStarredFeaturesCookie(this.state.starredFeatures)
    );
  };

  /** Single feature row (star + title + optional description); used under Favorites and feature areas. */
  renderFeatureMenuListItem = (f: any, fa: FeatureArea, listKey: string) => {
    if (!f.extendedAttributes) {
      return null;
    }
    const { classes } = this.props;
    const featureStarId =
      f.key || f.extendedAttributes.url || `${fa.title}::${f.title}`;
    const isStarred = !!this.state.starredFeatures[featureStarId];
    const showStars = this.state.showMenuStars;
    return (
      <ListItem
        key={listKey}
        className={f.key}
        button
        onClick={
          showStars
            ? this.toggleFeatureStar(featureStarId)
            : this.handleMenuItemClick(f.extendedAttributes.url, isStarred)
        }
      >
        {showStars && (
          <ListItemIcon className={classes['icon']}>
            <IconButton
              size="small"
              className={classes.featureStarButton}
              aria-label={isStarred ? 'Unstar feature' : 'Star feature'}
              onClick={this.toggleFeatureStar(featureStarId)}
            >
              {isStarred ? (
                <StarIcon fontSize="small" />
              ) : (
                <StarBorderOutlinedIcon fontSize="small" />
              )}
            </IconButton>
          </ListItemIcon>
        )}
        <ListItemText
          disableTypography
          className={classes['menuItem']}
          primary={<div className={classes['menuItem']}>{f.title}</div>}
          secondary={
            <div className={classes['menuItemDescription']}>
              {this.state.showVerboseMenus ? f.description : null}
            </div>
          }
        />
      </ListItem>
    );
  };

  render() {
    const { drawerOpen } = this.state;
    const { classes } = this.props;

    const featureAreas = PortalUserContext.featureAreas;
    var filterText = this.state.filterText;
    const titleMatchesFilter = (f: { title?: string }) => {
      if (!filterText) {
        return true;
      }
      return (f.title || '')
        .toLowerCase()
        .includes(filterText.toLowerCase());
    };

    const favoriteEntries: {
      f: any;
      fa: FeatureArea;
      featureStarId: string;
    }[] = [];
    const starred = this.state.starredFeatures;
    if (featureAreas && Object.keys(starred).length) {
      const seen = new Set<string>();
      featureAreas.forEach((fa) => {
        fa.features.forEach((f) => {
          if (!f.extendedAttributes || !f.extendedAttributes.url) {
            return;
          }
          const featureStarId =
            f.key || f.extendedAttributes.url || `${fa.title}::${f.title}`;
          if (!starred[featureStarId]) {
            return;
          }
          if (!titleMatchesFilter(f)) {
            return;
          }
          if (seen.has(featureStarId)) {
            return;
          }
          seen.add(featureStarId);
          favoriteEntries.push({ f, fa, featureStarId });
        });
      });
      favoriteEntries.sort((a, b) =>
        a.f.title.localeCompare(b.f.title, undefined, { sensitivity: 'base' })
      );
    }
    var sidx = 0;
    var menuListItems: any = null;
    if (featureAreas) {
      menuListItems = featureAreas.map((fa: FeatureArea, idx: number) => {
        var listIcon: any;
        var inset: boolean = true;

        var subAreas: any = {};

        fa.features.forEach((f) => {
          if (f.extendedAttributes && f.extendedAttributes.url) {
            var key = f.subAreaTitle ? f.subAreaTitle : 'none';
            if (!subAreas[key]) {
              subAreas[key] = {
                title: f.subAreaTitle,
                features: new Array<any>()
              };
            }

            if (titleMatchesFilter(f)) {
              subAreas[key].features.push(f);
            }
          }
        });

        if (fa.extendedAttributes && fa.extendedAttributes.icon) {
          if (fa.extendedAttributes.icon.indexOf("fa-") > -1) {
            listIcon = (
              <ListItemIcon color="primary" className={classes['icon']}>
                <i className={classes['faicon'] + ' fa ' + fa.extendedAttributes.icon} />
              </ListItemIcon>
            );
          } else if (fa.extendedAttributes.icon) {
            listIcon = (
              <ListItemIcon color="primary" className={classes['icon']}>
                <Icon>{fa.extendedAttributes.icon}</Icon>
              </ListItemIcon>
            );
          }

          inset = false;
        }

        let sal = new Array<any>();
        for (let key in subAreas) {
          sidx += 1;
          var sa = subAreas[key];
          if (sa.features.length == 0) {
            continue;
          }
          var fidx = 0;
          let fl = sa.features.map((f: any, idx: number) => {
            fidx += 1;

            if (!f.extendedAttributes) {
              return null;
            }

            const featureStarId =
              f.key || f.extendedAttributes.url || `${fa.title}::${f.title}`;
            return this.renderFeatureMenuListItem(
              f,
              fa,
              `feat-${featureStarId}-sa${sidx}-i${fidx}`
            );
          });

          var subheader: any;
          if (sa.title) {
            subheader = (
              <ListSubheader className={classes.nestedListSubheader}>
                {sa.title}
              </ListSubheader>
            );
          }

          sal.push(
            <List
              dense
              key={'sa' + sidx}
              subheader={subheader}
              className={classes.nestedFeatureList}
            >
              {fl}
            </List>
          );
        }

        if (sal.length == 0) {
          return null;
        }

        let button = false;
        let onClick: any;
        if (fa.extendedAttributes && fa.extendedAttributes.url) {
          onClick = this.handleMenuItemClick(fa.extendedAttributes.url);
        }

        const showSection = () =>{
          for (let key in subAreas) {
            if ((subAreas[key] || {features: []}).features.length > 0) {
              return true;
            }
          }
          return false;
        }

        return (
          <div>
          {showSection() &&
          <div key={'fa' + idx}> 
            
            <ListItem button={button} onClick={() => {
              var { featureAreaVisibility } = this.state;
              featureAreaVisibility[fa.title] = !featureAreaVisibility[fa.title]
              this.setState({
                featureAreaVisibility: featureAreaVisibility
              }, () => {
                this.setFeatureAreaVisibilityCookie(featureAreaVisibility)
              })

            }}>
              {listIcon}
              <ListItemText className={classes['featureArea']} inset={inset} primary={fa.title} />
              {this.state.featureAreaVisibility[fa.title] == true  && <ListItemIcon color="primary" className={classes.featureAreaToggle}><i className="fa fa-caret-down" /></ListItemIcon>}
            </ListItem>
            <div key={'fal-' + fa.title} style={{ display: this.state.featureAreaVisibility[fa.title] == true && showSection() ? 'none' : '' }}>
              {sal}
            </div>
          </div>}
          </div>
        );
      });
    }

    return (
      <div>
        <Drawer
          className="menu-drawer"
          open={drawerOpen}
          onClose={this.toggleDrawer(false)}
        >
          <div tabIndex={0} role="button">
            <div className={classes['drawerWidth']}>
              <List data-name="icg-menu" id={"icg-menu"}>
                {!this.state.filteringDisabled &&<ListItem>
                  <TextField
                  autoFocus
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={{ borderRadius: 3 }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => this.setState({ filterText: '' })} edge="end" aria-label="clear input">
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    label="Filter by Title"
                    prompt="Feature Title"
                    value={this.state.filterText}
                    onChange={(e) => this.setState({ filterText: e.target.value })}
                    variant="outlined"
                    size="small"
                  />
                </ListItem>}

                <ListItem button onClick={this.handleMenuItemClick('/')}>
                  <ListItemIcon className={classes['icon']}>
                    <Icon>home</Icon>
                  </ListItemIcon>
                  <ListItemText primary={translate("Home")} />
                </ListItem>
                {favoriteEntries.length > 0 && (
                  <React.Fragment>
                    <ListItem>
                      <ListItemIcon className={classes['icon']}>
                        <Icon>star</Icon>
                      </ListItemIcon>
                      <ListItemText primary={translate('Favorites')} />
                    </ListItem>
                    <List
                      dense
                      disablePadding
                      className={classes.favoritesNestedList}
                    >
                      {favoriteEntries.map((entry) =>
                        this.renderFeatureMenuListItem(
                          entry.f,
                          entry.fa,
                          `fav-${entry.featureStarId}`
                        )
                      )}
                    </List>
                  </React.Fragment>
                )}
                {menuListItems}
                <Divider />
                <ListItem>
                  <ListItemText primary="Verbose" />
                  <ListItemSecondaryAction>
                    <Switch
                      onChange={this.toggleCompact}
                      checked={this.state.showVerboseMenus}
                      color="secondary"
                    />
                  </ListItemSecondaryAction>
                </ListItem>

                <ListItem>
                  <ListItemText primary="Disable Menu Filtering" />
                  <ListItemSecondaryAction>
                    <Switch
                      onChange={this.toggleFiltering}
                      checked={this.state.filteringDisabled}
                      color="secondary"
                    />
                  </ListItemSecondaryAction>
                </ListItem>

                <ListItem>
                  <ListItemText primary={translate('Show Menu Favorites Selector')} />
                  <ListItemSecondaryAction>
                    <Switch
                      onChange={this.toggleShowMenuStars}
                      checked={this.state.showMenuStars}
                      color="secondary"
                    />
                  </ListItemSecondaryAction>
                </ListItem>

              </List>
            </div>
          </div>
        </Drawer>
      </div>
    );
  }
}

export default withStyles(styles)(MenuDrawer);
